/**
 * SubmitEmojiDialog — public-facing form for signed-in users to
 * propose a new emoji for the gallery.
 *
 * Flow:
 *   1. User picks an image (≥64×64, ≤2MB, PNG/JPG/WebP/GIF).
 *   2. Adds a label, optional description, optional category suggestions.
 *   3. Submits — file goes straight to GridFS, record created with
 *      status="pending". Admin reviews via the Moderation tab.
 *   4. User can see status in the My Submissions panel.
 */
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Upload, Loader2, X, ImageOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 2 * 1024 * 1024;

const SubmitEmojiDialog = ({ open, onClose, onSubmitted }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLabel('');
    setDescription('');
    setCategories('');
  };

  const closeAndReset = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Image files only (PNG / JPG / WebP / GIF).');
    if (f.size > MAX_BYTES) return toast.error(`Max ${MAX_BYTES / (1024 * 1024)} MB — your file is ${(f.size / 1024 / 1024).toFixed(2)} MB.`);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return toast.error('Pick an image first.');
    if (label.trim().length < 2) return toast.error('Label must be at least 2 characters.');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', label.trim());
      fd.append('description', description.trim());
      fd.append('categories', categories.trim());
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/emojis/submissions`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Submission failed');
      toast.success('Thanks! Your emoji is pending review.');
      reset();
      onSubmitted?.(data.submission);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeAndReset()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="submit-emoji-dialog">
        <DialogHeader>
          <DialogTitle>Submit a new emoji</DialogTitle>
          <DialogDescription>
            Got an idea for a communication emoji? Upload a PNG/JPG/WebP/GIF and we&apos;ll review it.
            Approved emojis show up in the gallery and you&apos;ll be credited as the submitter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Image picker */}
          <div>
            <Label className="text-xs">Image (≤2 MB, at least 64×64 px)</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="submit-emoji-file-input"
            />
            {!preview ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full mt-1 border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 flex flex-col items-center gap-2 text-muted-foreground hover:border-[#00A896] hover:text-[#00A896] transition"
                data-testid="submit-emoji-pick-btn"
              >
                <Upload className="h-6 w-6" />
                <span className="text-sm">Click to choose an image</span>
              </button>
            ) : (
              <div className="mt-1 relative border rounded-lg p-3 flex items-center gap-3">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-16 h-16 object-contain border bg-white rounded"
                />
                <div className="flex-1 min-w-0 text-xs">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[#00A896] hover:underline text-xs mt-0.5"
                    data-testid="submit-emoji-replace-btn"
                  >
                    Replace
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => { setFile(null); setPreview(null); }}
                  className="text-muted-foreground hover:text-red-500"
                  aria-label="Remove image"
                  data-testid="submit-emoji-remove-btn"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Label */}
          <div>
            <Label htmlFor="sub-label" className="text-xs">Name <span className="text-red-500">*</span></Label>
            <Input
              id="sub-label"
              placeholder="e.g. Sensory overload"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 60))}
              maxLength={60}
              data-testid="submit-emoji-label-input"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">{label.length}/60</p>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="sub-desc" className="text-xs">What does it mean? <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea
              id="sub-desc"
              rows={2}
              placeholder="A short description so the reviewer understands what your emoji communicates."
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 280))}
              maxLength={280}
              data-testid="submit-emoji-desc-input"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">{description.length}/280</p>
          </div>

          {/* Categories */}
          <div>
            <Label htmlFor="sub-cats" className="text-xs">Suggested categories <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              id="sub-cats"
              placeholder="comma-separated, e.g. mood, sensory, request"
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              data-testid="submit-emoji-cats-input"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">Reviewers can adjust these before publishing.</p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={closeAndReset} disabled={submitting} data-testid="submit-emoji-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !file || label.trim().length < 2}
            className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
            data-testid="submit-emoji-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Submit for review</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitEmojiDialog;
export { ImageOff };
